import React, { createContext, useContext, useEffect, useState } from 'react'
import { connectSocket, disconnectSocket, getSocket } from './socketClient'
import { useRealtimeSync } from './useRealtimeSync'
import { getAccessToken } from '../utils/tokenStorage'

interface RealtimeContextValue {
  isConnected: boolean
  lastEvent: { event: string; timestamp: Date; payload?: unknown } | null
}

const RealtimeContext = createContext<RealtimeContextValue>({
  isConnected: false,
  lastEvent: null,
})

export const useRealtime = () => useContext(RealtimeContext)

export function RealtimeProvider({ children }: { children: React.ReactNode }) {
  const [tokenPresent, setTokenPresent] = useState(() => Boolean(getAccessToken()))
  const { isConnected, lastEvent } = useRealtimeSync()

  // Track token existence in localStorage (handles login / logout)
  useEffect(() => {
    const checkToken = () => {
      const hasToken = Boolean(getAccessToken())
      setTokenPresent(hasToken)
      if (hasToken) {
        connectSocket()
      } else {
        disconnectSocket()
      }
    }

    checkToken()

    window.addEventListener('storage', checkToken)
    return () => {
      window.removeEventListener('storage', checkToken)
    }
  }, [])

  // Connect on initial load if token exists
  useEffect(() => {
    if (tokenPresent) {
      connectSocket()
    }
  }, [tokenPresent])

  return (
    <RealtimeContext.Provider value={{ isConnected, lastEvent }}>
      {children}
    </RealtimeContext.Provider>
  )
}
